import { supabase } from './supabase';
import { logger } from './logger';

export type AnalysisVerdict = 'approve' | 'review' | 'reject';

export interface AnalysisReport {
  analyzed_at?: string;
  /** 'image' : pipeline réduit (pas d'audio, pas de musique/geste/style). */
  media_type?: 'video' | 'image';
  rights_consent_at?: string | null;
  transcript?: string;
  frames?: string[];
  moderation?: { flagged: boolean; categories: string[] };
  conformity?: {
    conformity_score?: number;
    is_work_related?: boolean;
    summary?: string;
  } | null;
  technique?: {
    technique_score?: number | null;
    criteria?: { name: string; score: number | null; comment: string }[];
  } | null;
  brands?: {
    detected: boolean;
    brands?: { name: string; confidence: number; context: string }[];
    summary?: string;
    error?: string;
  } | null;
  music?: {
    licensed: boolean;
    skipped?: boolean;
    error?: string | null;
    title?: string | null;
    artist?: string | null;
    album?: string | null;
    label?: string | null;
    song_link?: string | null;
    catalogs?: string[];
  } | null;
  style?: {
    mood?: Record<string, number>;
    humour?: number;
    vocabulary?: Record<string, number>;
    rythme?: Record<string, number>;
    recit?: Record<string, number>;
    adresse?: Record<string, number>;
  } | null;
  verdict?: AnalysisVerdict;
}

export interface AdminVideo {
  id: string;
  source: 'candidate' | 'employer';
  candidateId: string;
  candidateName: string;
  videoUrl: string;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  /** 'video' | 'image' — un post image se prévisualise en <img>, pas en <video> */
  mediaType: 'video' | 'image';
  textOverlay: string | null;
  status: 'uploading' | 'analyzing' | 'pending_review' | 'approved' | 'rejected';
  rejectionReason: string | null;
  reviewedAt: string | null;
  submittedAt: string;
  createdAtMs: number;
  tags: string[] | null;
  /** Horodatage du consentement droits / droit à l'image (null = non recueilli). */
  rightsConsentAt: string | null;
  muxPlaybackId: string | null;
  analysisStatus: 'pending' | 'running' | 'done' | 'failed' | null;
  analysisVerdict: AnalysisVerdict | null;
  analysisReport: AnalysisReport | null;
}

/**
 * URL lisible dans le navigateur : les URLs HLS Mux (.m3u8) ne sont pas
 * lues nativement par Chrome/Firefox → rendition mp4 statique.
 * Les URLs héritées (Supabase Storage) sont renvoyées telles quelles.
 */
export function webPlayableUrl(url: string): string {
  const match = url?.match(/^https:\/\/stream\.mux\.com\/([^/.]+)\.m3u8/);
  if (!match) return url;
  return `https://stream.mux.com/${match[1]}/highest.mp4`;
}

function mapVideoRow(v: any, source: 'candidate' | 'employer', name: string, ownerId: string): AdminVideo {
  const createdMs = v.created_at ? new Date(v.created_at).getTime() : 0;
  return {
    id: v.id,
    source,
    candidateId: ownerId,
    candidateName: name,
    videoUrl: v.video_url,
    thumbnailUrl: v.thumbnail_url,
    durationSeconds: v.duration_seconds,
    mediaType: v.media_type === 'image' ? 'image' : 'video',
    textOverlay: v.text_overlay || null,
    status: v.status,
    rejectionReason: v.rejection_reason || null,
    reviewedAt: v.reviewed_at ? new Date(v.reviewed_at).toLocaleDateString('fr-FR') : null,
    submittedAt: createdMs ? new Date(createdMs).toLocaleString('fr-FR') : '',
    createdAtMs: createdMs,
    tags: v.tags,
    rightsConsentAt: v.rights_consent_at
      ? new Date(v.rights_consent_at).toLocaleString('fr-FR')
      : null,
    muxPlaybackId: v.mux_playback_id || null,
    analysisStatus: v.analysis_status || null,
    analysisVerdict: v.analysis_verdict || null,
    analysisReport: v.analysis_report || null,
  };
}

export async function fetchVideos(): Promise<AdminVideo[]> {
  const [candRes, empRes] = await Promise.all([
    supabase
      .from('candidate_videos')
      .select(`
        id,
        candidate_id,
        video_url,
        thumbnail_url,
        duration_seconds,
        media_type,
        text_overlay,
        status,
        rejection_reason,
        reviewed_at,
        created_at,
        tags,
        rights_consent_at,
        mux_playback_id,
        analysis_status,
        analysis_verdict,
        analysis_report,
        candidate_profiles!candidate_videos_candidate_id_fkey ( first_name, last_name )
      `)
      .order('created_at', { ascending: false }),
    supabase
      .from('employer_videos')
      .select(`
        id,
        employer_id,
        video_url,
        thumbnail_url,
        duration_seconds,
        media_type,
        text_overlay,
        status,
        rejection_reason,
        reviewed_at,
        created_at,
        tags,
        rights_consent_at,
        mux_playback_id,
        analysis_status,
        analysis_verdict,
        analysis_report,
        title,
        employer_profiles!employer_videos_employer_id_fkey ( company_name, first_name, last_name )
      `)
      .order('created_at', { ascending: false }),
  ]);

  if (candRes.error) {
    logger.error('fetch_videos', 'videoService', { error: candRes.error.message });
    throw candRes.error;
  }
  if (empRes.error) {
    logger.error('fetch_employer_videos', 'videoService', { error: empRes.error.message });
    throw empRes.error;
  }

  const candidates = (candRes.data || []).map((v: any) => {
    const p = v.candidate_profiles;
    const name = `${p?.first_name || ''} ${p?.last_name || ''}`.trim() || 'Candidat';
    return mapVideoRow(v, 'candidate', name, v.candidate_id);
  });

  const employers = (empRes.data || []).map((v: any) => {
    const p = v.employer_profiles;
    const company = p?.company_name || '';
    const person = `${p?.first_name || ''} ${p?.last_name || ''}`.trim();
    const name = [company, person].filter(Boolean).join(' · ') || v.title || 'Recruteur';
    return mapVideoRow(v, 'employer', name, v.employer_id);
  });

  const all = [...candidates, ...employers].sort((a, b) => b.createdAtMs - a.createdAtMs);
  logger.technical('fetch_videos_ok', 'videoService', {
    candidates: candidates.length,
    employers: employers.length,
  });
  return all;
}

function videoTable(source: 'candidate' | 'employer') {
  return source === 'employer' ? 'employer_videos' : 'candidate_videos';
}

/** Relance l'analyse IA d'une publication (vidéo Mux ou post image) — admin JWT accepté par analyze-video. */
export async function requestAnalysis(videoId: string, source: 'candidate' | 'employer' = 'candidate'): Promise<void> {
  logger.functional('request_analysis', 'videoService', { videoId, source });
  const { error } = await supabase.functions.invoke('analyze-video', {
    body: { video_id: videoId, source },
  });
  if (error) {
    logger.error('request_analysis', 'videoService', { videoId, source, error: error.message });
    throw error;
  }
}

// ─── Réglages de modération (gating hybride) ─────────────────────────

export interface ModerationSettings {
  mode: 'human_first' | 'ai_auto';
  autoApproveThreshold: number;
  autoRejectThreshold: number;
}

export async function fetchModerationSettings(): Promise<ModerationSettings> {
  const { data, error } = await supabase
    .from('moderation_settings')
    .select('mode, auto_approve_threshold, auto_reject_threshold')
    .eq('id', true)
    .single();
  if (error) {
    logger.error('fetch_moderation_settings', 'videoService', { error: error.message });
    throw error;
  }
  return {
    mode: data.mode,
    autoApproveThreshold: data.auto_approve_threshold,
    autoRejectThreshold: data.auto_reject_threshold,
  };
}

export async function updateModerationSettings(settings: ModerationSettings): Promise<void> {
  logger.functional('update_moderation_settings', 'videoService', { ...settings });
  const { error } = await supabase
    .from('moderation_settings')
    .update({
      mode: settings.mode,
      auto_approve_threshold: settings.autoApproveThreshold,
      auto_reject_threshold: settings.autoRejectThreshold,
      updated_at: new Date().toISOString(),
    })
    .eq('id', true);
  if (error) {
    logger.error('update_moderation_settings', 'videoService', { error: error.message });
    throw error;
  }
}

export async function approveVideo(
  videoId: string,
  adminId: string,
  source: 'candidate' | 'employer' = 'candidate',
): Promise<void> {
  logger.functional('approve_video', 'videoService', { videoId, adminId, source });
  const { error } = await supabase
    .from(videoTable(source))
    .update({
      status: 'approved',
      reviewed_by: adminId,
      reviewed_at: new Date().toISOString(),
    })
    .eq('id', videoId);

  if (error) {
    logger.error('approve_video', 'videoService', { videoId, source, error: error.message });
    throw error;
  }
  logger.technical('approve_video_ok', 'videoService', { videoId, source });
}

export async function rejectVideo(
  videoId: string,
  adminId: string,
  reason: string,
  source: 'candidate' | 'employer' = 'candidate',
): Promise<void> {
  logger.functional('reject_video', 'videoService', { videoId, adminId, reason, source });
  const { error } = await supabase
    .from(videoTable(source))
    .update({
      status: 'rejected',
      reviewed_by: adminId,
      reviewed_at: new Date().toISOString(),
      rejection_reason: reason,
    })
    .eq('id', videoId);

  if (error) {
    logger.error('reject_video', 'videoService', { videoId, source, error: error.message });
    throw error;
  }
  logger.technical('reject_video_ok', 'videoService', { videoId, source });
}

export async function unpublishVideo(
  videoId: string,
  adminId: string,
  source: 'candidate' | 'employer' = 'candidate',
): Promise<void> {
  logger.functional('unpublish_video', 'videoService', { videoId, adminId, source });
  const { error } = await supabase
    .from(videoTable(source))
    .update({
      status: 'pending_review',
      reviewed_by: adminId,
      reviewed_at: new Date().toISOString(),
      rejection_reason: null,
    })
    .eq('id', videoId);

  if (error) {
    logger.error('unpublish_video', 'videoService', { videoId, source, error: error.message });
    throw error;
  }
  logger.technical('unpublish_video_ok', 'videoService', { videoId, source });
}
